/*
  <ugc-player> - mini UGC player for the PDP.

  Tiles open a native <dialog> overlay that plays customer clips story-style:
  deferred video load (data-src injected on open, so nothing loads above the
  fold), auto-advance on end, swipe / tap-zone / arrow-key nav, and a mute
  toggle. The shoppable card inside each slide is the theme's own <product-form>,
  so add-to-cart + cart drawer keep working with no extra wiring here.
*/
class UgcPlayer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('.ugc-player__dialog');
    this.slides = Array.from(this.querySelectorAll('[data-ugc-slide]'));
    this.segments = Array.from(this.querySelectorAll('.ugc-player__progress-seg'));
    this.muteBtn = this.querySelector('[data-ugc-mute]');
    this.current = 0;
    this.muted = true;
    this.demoTimer = null;

    // Tiles autoplay (muted) while scrolled into view - independent of the overlay.
    this.setupTileAutoplay();

    if (!this.dialog || !this.slides.length) return;

    this.querySelectorAll('[data-ugc-open]').forEach((btn) => {
      btn.addEventListener('click', () => this.open(parseInt(btn.dataset.ugcOpen, 10)));
    });
    this.querySelector('[data-ugc-close]')?.addEventListener('click', () => this.dialog.close());
    this.querySelector('[data-ugc-prev]')?.addEventListener('click', () => this.go(-1));
    this.querySelector('[data-ugc-next]')?.addEventListener('click', () => this.go(1));
    this.muteBtn?.addEventListener('click', () => this.toggleMute());

    this.dialog.addEventListener('close', () => this.stop());
    this.dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') this.go(1);
      if (e.key === 'ArrowLeft') this.go(-1);
    });
    this.bindSwipe();
  }

  disconnectedCallback() {
    this.tileObserver?.disconnect();
  }

  setupTileAutoplay() {
    const tiles = Array.from(this.querySelectorAll('[data-ugc-tile]'));
    if (!tiles.length || !('IntersectionObserver' in window)) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.tileObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            video.muted = true;
            const playback = video.play();
            if (playback && playback.catch) playback.catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    tiles.forEach((video) => this.tileObserver.observe(video));
  }

  open(index) {
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
    this.activate(index);
  }

  activate(index) {
    this.current = Math.max(0, Math.min(index, this.slides.length - 1));
    this.clearTimers();

    this.slides.forEach((slide, i) => {
      const video = slide.querySelector('.ugc-player__video');
      const active = i === this.current;
      slide.hidden = !active;
      if (video && !active) {
        video.pause();
      }
    });

    this.segments.forEach((seg, i) => {
      seg.classList.toggle('is-done', i < this.current);
      const bar = seg.querySelector('i');
      if (bar) bar.style.width = i < this.current ? '100%' : '0';
    });

    const slide = this.slides[this.current];

    const video = slide.querySelector('.ugc-player__video');
    if (video && video.dataset.src) {
      if (!video.src) video.src = video.dataset.src;
      video.muted = this.muted;
      video.currentTime = 0;
      video.onended = () => this.go(1);
      video.ontimeupdate = () => this.tick(video);
      const playback = video.play();
      if (playback && playback.catch) playback.catch(() => {});
    } else {
      // Demo / no media - run a fixed-length timed progress, then advance.
      this.runDemoProgress();
    }
  }

  tick(video) {
    if (!video.duration) return;
    const bar = this.segments[this.current]?.querySelector('i');
    if (bar) bar.style.width = `${Math.min(100, (video.currentTime / video.duration) * 100)}%`;
  }

  runDemoProgress() {
    const bar = this.segments[this.current]?.querySelector('i');
    if (!bar) return;
    const start = performance.now();
    const duration = 4000;
    const step = (now) => {
      const pct = Math.min(100, ((now - start) / duration) * 100);
      bar.style.width = `${pct}%`;
      if (pct < 100) {
        this.demoRaf = requestAnimationFrame(step);
      } else {
        this.go(1);
      }
    };
    this.demoRaf = requestAnimationFrame(step);
  }

  go(direction) {
    const next = this.current + direction;
    if (next < 0) return;
    if (next >= this.slides.length) {
      this.dialog.close();
      return;
    }
    this.activate(next);
  }

  toggleMute() {
    this.muted = !this.muted;
    const video = this.slides[this.current]?.querySelector('.ugc-player__video');
    if (video) video.muted = this.muted;
    if (this.muteBtn) {
      this.muteBtn.setAttribute('aria-pressed', String(!this.muted));
      this.muteBtn.setAttribute('aria-label', this.muted ? 'Unmute video' : 'Mute video');
    }
  }

  stop() {
    this.clearTimers();
    const video = this.slides[this.current]?.querySelector('.ugc-player__video');
    if (video) {
      video.pause();
      video.ontimeupdate = null;
      video.onended = null;
    }
  }

  clearTimers() {
    if (this.demoRaf) cancelAnimationFrame(this.demoRaf);
    this.demoRaf = null;
  }

  bindSwipe() {
    let startX = null;
    let startY = null;
    const stage = this.querySelector('.ugc-player__stage');
    if (!stage) return;
    stage.addEventListener('pointerdown', (e) => {
      startX = e.clientX;
      startY = e.clientY;
    });
    stage.addEventListener('pointerup', (e) => {
      if (startX === null) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        this.go(dx < 0 ? 1 : -1);
      } else if (dy > 80 && Math.abs(dy) > Math.abs(dx)) {
        this.dialog.close();
      }
      startX = null;
      startY = null;
    });
  }
}

customElements.define('ugc-player', UgcPlayer);
